// FlagCallModal.js
// User-initiated "Help improve Maia" consent modal per
// USER_FLAGGED_CALL_IMPROVEMENT.md Phase C. Copy version is bound to
// MODAL_VERSION below — bump it whenever the body copy changes substantively.

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../FirebaseConfig';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
} from '@mui/material';

// Bumping this string is the trigger for re-consent on existing flagged docs;
// older consent records keep their own version string so we can answer
// "what did the user actually agree to?" later. Keep in sync with the
// _ACCEPTED_MODAL_VERSIONS set in voiceAgent/server.py.
export const FLAG_MODAL_VERSION = 'v1-2026-04-26';

const VOICE_AGENT_BASE = 'https://maia-voice-agent-370081006843.us-central1.run.app';
const REASON_MAX_LEN = 200;

const FlagCallModal = ({ open, onClose, currentUser, callHistoryId, contactName, onFlagged }) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  // null = still checking; true = user has flagged before (skip consent
  // copy + checkbox); false = first-time user (full consent flow).
  // Once a user has consented under FLAG_MODAL_VERSION at least once, we
  // don't make them read the same legal-style copy again — we just take
  // their additional comments.
  const [hasConsentedBefore, setHasConsentedBefore] = useState(null);

  // On modal open, query for any prior flagged_calls doc this user owns.
  // Quick (one-doc-limit query, indexed by uid via the (uid, flagged_at)
  // composite); fail-open to the full consent flow if the query errors.
  useEffect(() => {
    if (!open || !currentUser) {
      setHasConsentedBefore(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const flaggedRef = collection(db, 'flagged_calls');
        const q = query(flaggedRef, where('uid', '==', currentUser.uid), limit(1));
        const snap = await getDocs(q);
        if (!cancelled) setHasConsentedBefore(!snap.empty);
      } catch (e) {
        console.error('FlagCallModal: prior-consent check failed', e);
        if (!cancelled) setHasConsentedBefore(false); // fail open to full consent
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentUser]);

  const reset = () => {
    setConsentChecked(false);
    setReason('');
    setErrorText('');
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    // Returning users are auto-consented (their prior flag's consent record
    // covers this submission under the same FLAG_MODAL_VERSION). First-time
    // users still must check the box.
    const consentGate = hasConsentedBefore === true || consentChecked;
    if (!consentGate || submitting) return;
    setSubmitting(true);
    setErrorText('');
    try {
      const idToken = await currentUser.getIdToken();
      const resp = await axios.post(
        `${VOICE_AGENT_BASE}/flag-call`,
        {
          call_history_id: callHistoryId,
          user_reason: reason,
          consent: {
            consent_given: true,
            modal_version: FLAG_MODAL_VERSION,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const flagId = resp?.data?.flag_id;
      if (onFlagged) onFlagged(flagId);
      reset();
      onClose();
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setErrorText(apiMsg || 'Could not submit your flag. Please try again.');
      setSubmitting(false);
    }
  };

  // First-time user → full consent flow with paragraphs + checkbox.
  // Returning user (has flagged at least one prior call under the current
  // modal version) → simplified flow with just the comment field.
  // While we're still loading the consent state, render the conservative
  // (full-consent) layout so the modal isn't empty for the brief moment
  // before the query resolves.
  const isReturningUser = hasConsentedBefore === true;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isReturningUser ? 'Help improve Maia' : 'Help improve Maia with this call?'}
      </DialogTitle>
      <DialogContent>
        {contactName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Call to <strong>{contactName}</strong>
          </Typography>
        )}

        {isReturningUser ? (
          // Returning-user flow: prior consent under FLAG_MODAL_VERSION still
          // applies; we don't make them re-read the same paragraphs. Just ask
          // for additional comments.
          <DialogContentText sx={{ mb: 2 }}>
            Thanks for helping improve Maia. Please add any comments or
            additional information you believe would improve Maia relating to
            this call or how Maia functions generally.
          </DialogContentText>
        ) : (
          // First-time-user flow: full consent text + checkbox below.
          <>
            <DialogContentText sx={{ mb: 1.5 }}>
              We'd like to use this call's transcript to help improve how Maia
              handles calls like yours. Before we do, here's exactly what that
              means:
            </DialogContentText>

            <DialogContentText sx={{ mb: 1.5 }}>
              <strong>What's shared:</strong> The text transcript of this call,
              with sensitive values removed. Account numbers, PINs, SSN digits,
              date of birth, and phone numbers are replaced with placeholders
              (e.g., "Maia entered &lt;ACCOUNT_NUMBER&gt;") before the transcript
              reaches any analysis.
            </DialogContentText>

            <DialogContentText sx={{ mb: 1.5 }}>
              <strong>What's NOT shared:</strong> Nothing audio. No raw recordings.
              No other calls you've made. The values that would identify you or
              your account never leave our backend.
            </DialogContentText>

            <DialogContentText sx={{ mb: 1.5 }}>
              <strong>How it's used:</strong> Automated AI tooling analyzes the
              redacted transcript and proposes improvements to Maia — new test
              cases, prompt refinements, or rule updates. The Maia team reviews
              those proposed improvements only, not the underlying transcript.
              No human developer reads your flagged call.
            </DialogContentText>

            <DialogContentText sx={{ mb: 2 }}>
              <strong>Your control:</strong> Your flagged call transcript is
              available for up to 30 days. You may delete it at any time sooner
              from your call history; deletion is immediate.
            </DialogContentText>
          </>
        )}

        <TextField
          label={isReturningUser ? 'Comments' : 'What went wrong? (optional — helps us prioritize)'}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX_LEN))}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          margin="normal"
          inputProps={{ maxLength: REASON_MAX_LEN }}
          helperText={`${reason.length}/${REASON_MAX_LEN}`}
        />

        {!isReturningUser && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
            }
            label="I understand what's being shared and consent to use this call to improve Maia."
          />
        )}

        {errorText && (
          <Box sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}>
            {errorText}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={(!isReturningUser && !consentChecked) || submitting}
        >
          {submitting ? 'Submitting…' : 'Submit flag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlagCallModal;
